import {AfterViewInit, Component, isDevMode, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {environment} from "../../../../environments/environment";
import {MatTableDataSource} from "@angular/material/table";
import {Centre} from "../../../interfaces/Centres";
import {Observable, of, Subject} from "rxjs";
import {FullChild} from "../../../interfaces/FullChild";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {FormControl} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {CentreService} from "../../../services/centre.service";
import {ChildService} from "../../child/child.service";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {MatSelectChange} from "@angular/material/select";
import firebase from "firebase/compat";
import {takeUntil} from "rxjs/internal/operators/takeUntil";
import {child} from "@angular/fire/database";
import {Hospitals} from "../../../interfaces/Hospitals";
import {FinancialYearService} from "../../../services/financial-year.service";
import {HospitalService} from "../../../services/hospital.service";
import {map} from "rxjs/operators";
import {CounselingService} from "../../../modules/counseling/counseling.service";
import {EducationService} from "../../../modules/education/education.service";

interface RankingData{
  Occupancy: number;
  centerName: string;
  centreCode: string;
  startDate: any; // Assuming Timestamp type
    endDate: any; // Assuming Timestamp type
    totalRooms: number;
  weekId: number;
  avg?:number;
}
@Component({
    selector: 'app-mis2',
    templateUrl: './mis2.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./mis2.component.scss']
})
export class Mis2Component implements OnInit, OnDestroy, AfterViewInit {
  env = isDevMode() ? environment.testMode : environment.productionMode;
    @ViewChild(MatSort) sort!: MatSort;
    mStartDate?: Date | null
    mEndDate?: Date | null
    show = false  //For refetching tables and graphs
    dataSource: MatTableDataSource<Centre>;
    /**
     * The date the platform went live. Every session query on this dashboard is
     * bounded by it, so a "Till Date" figure is this date onwards plus
     * PRE_SYSTEM_SESSIONS below.
     */
    readonly systemGoLiveDate = new Date('2025-02-01')

    /**
     * Sessions delivered before the platform went live. They were never recorded in
     * Firestore, so no query can return them — they are carried here and added to the
     * "Till Date" tiles. Supplied by the programme team, grouped by financial year;
     * these are historical facts, not derived values, and must not be recalculated.
     */
    readonly PRE_SYSTEM_SESSIONS = {
        counselling: {
            fy2021_23: 3838,
            fy2023_24: 5789,
            fy2024_25: {total: 1979, individual: 1492, group: 64},
        },
        education: {
            fy2022_23: 3258,
            fy2023_24: 6768,
            fy2024_25: {total: 3155, individual: 2720, group: 388},
        },
    }

    /** Session counts bounded to the current financial year (DASH-7). */
    mFyCounsellingIndividual = 0
    mFyCounsellingGroup = 0
    mFyCounsellingAssessment = 0
    mFyCounsellingFollowUpAssessment = 0
    mFyEducationIndividual = 0
    mFyEducationGroup = 0

    /** e.g. "2026-2027" — derived, so the tiles relabel themselves each April. */
    get currentFinancialYearLabel(): string {
        const fy = this.financialYearService.getCurrentFinancialYear();
        return `${fy.startDate.getFullYear()}-${fy.endDate.getFullYear()}`;
    }

    /**
     * What the session cards are actually showing: the picked date range, or the
     * financial year when no range is set.
     */
    get filteredRangeLabel(): string {
        if (this.mStartDate && this.mEndDate) {
            const fmt = (d: Date) => d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
            return `${fmt(this.mStartDate)} – ${fmt(this.mEndDate)}`;
        }
        return this.currentFinancialYearLabel;
    }

    /** Live counts since go-live, plus every pre-platform financial year. */
    get counsellingTotalTillDate(): number {
        const pre = this.PRE_SYSTEM_SESSIONS.counselling;
        return this.mTotalCounsellingIndividual + this.mTotalCounsellingGroup
            + pre.fy2021_23 + pre.fy2023_24 + pre.fy2024_25.total;
    }

    get educationTotalTillDate(): number {
        const pre = this.PRE_SYSTEM_SESSIONS.education;
        return this.mTotalEducationIndividual + this.mTotalEducationGroup
            + pre.fy2022_23 + pre.fy2023_24 + pre.fy2024_25.total;
    }

    /**
     * Current financial year only. No pre-platform figure is added: the platform has
     * been live since 2025-02-01, so every session in the current FY is on record.
     */
    get counsellingTotalCurrentFy(): number {
        return this.mFyCounsellingIndividual + this.mFyCounsellingGroup
            + this.mFyCounsellingAssessment + this.mFyCounsellingFollowUpAssessment;
    }

    get educationTotalCurrentFy(): number {
        return this.mFyEducationIndividual + this.mFyEducationGroup;
    }

    // ── Firestore read accounting ─────────────────────────────────────────────
    // This is the heaviest screen in the app, so what it costs should be visible.
    // Everything tracked here is billed reads, broken down by what asked for them.
    private readonly readTally = new Map<string, number>();
    private readSummaryTimer: any;

    /** Documents actually returned by a query. */
    private trackReads(source: string, docs: number, note = ''): void {
        this.readTally.set(source, (this.readTally.get(source) ?? 0) + docs);
        console.log(`📄 [MIS2] ${source} — ${docs} doc read${docs === 1 ? '' : 's'}${note ? ` (${note})` : ''}`);
        this.scheduleReadSummary();
    }

    /**
     * Queries whose service memoises the result with shareReplay: only the first
     * subscriber pays, so later emissions must not be counted again.
     */
    private trackCachedReads(source: string, docs: number): void {
        if (this.readTally.has(source)) {
            console.log(`♻️ [MIS2] ${source} — ${docs} docs replayed from the service cache, 0 new reads`);
            return;
        }
        this.trackReads(source, docs, 'first fetch; later subscribers reuse the cache');
    }

    /**
     * count() returns no documents — Firestore bills one read per 1000 index entries
     * matched, minimum one. Reported separately so the tally is not read as bytes.
     */
    private trackAggregation(source: string, matched: number): void {
        const billed = Math.max(1, Math.ceil(matched / 1000));
        this.readTally.set(source, (this.readTally.get(source) ?? 0) + billed);
        console.log(`🔢 [MIS2] ${source} — counted ${matched}, ~${billed} billed read${billed === 1 ? '' : 's'}`);
        this.scheduleReadSummary();
    }

    /** Prints once the dashboard stops asking for things, heaviest source first. */
    private scheduleReadSummary(): void {
        clearTimeout(this.readSummaryTimer);
        this.readSummaryTimer = setTimeout(() => {
            const rows = [...this.readTally.entries()].sort((a, b) => b[1] - a[1]);
            const total = rows.reduce((sum, [, reads]) => sum + reads, 0);
            console.log(`%c📄 MIS2 — ${total} Firestore reads to render this dashboard`,
                'font-weight:bold;font-size:13px');
            console.table(rows.map(([source, reads]) => ({
                source,
                reads,
                share: total ? `${((reads / total) * 100).toFixed(1)}%` : '—'
            })));
        }, 1500);
    }
    dataTableLoaded = false;
    dataHospitalLoaded = false;
    centreSelectionArray = [
        {
            centreName: 'All Centres',
            centreCode: 'All'
        },
        // {
        //     centreName: 'AL Bandra',
        //     centreCode: 'ALBandra'
        // },
        {
            centreName: 'AL Ahmedabad',
            centreCode: 'ALAhmedabad'
        },
        {
            centreName: 'AL Bangalore',
            centreCode: 'ALBangalore'
        },
      {
        centreName: 'AL Borivali',
        centreCode: 'ALBorivali'
      },
        {
            centreName: 'AL Chandigarh',
            centreCode: 'ALChandigarh'
        },

      {
        centreName: 'AL Coimbatore',
        centreCode: 'ALCoimbatore'
      },
       {
            centreName: 'AL Gurunanak Niwas',
            centreCode: 'ALGurunanak'
        },
        {
            centreName: 'AL Diamond Garden',
            centreCode: 'ALDiamondGarden'
        },
        {
            centreName: 'AL Manipal',
            centreCode: 'ALManipal'
        }, {
            centreName: 'AL Pune',
            centreCode: 'ALPune'
        },
        {
            centreName: 'AL Sandu Garden',
            centreCode: 'ALSanduGarden'
        },

    ]
    userData: any
    private _destroyed$ = new Subject()
    mCentres: Centre[] = []
    allCentres: any[] = [];
    extraRowAdded = false
    mChildren: FullChild[] = []
    mAllChildren: FullChild[] = []
    mAdmissionChartCentres: string[] = []
    mAdmissionChartUniqueCount: number[] = []
    mAdmissionChartFollowUpCount: number[] = []
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) matSort!: MatSort;
    displayedColumns: String[] = ['sr','centername', 'totalUnits', 'occupiedUnits', 'availableUnits', 'centrePercentage','lastSync'];
    //*********** For Occupancy card******************
    totalUnits = 0
    totalOccupiedUnits = 0
    totalAvailableUnits = 0
    centrePercentage = 0
    totalRanking=0
//***********************
//************CHILD DATA************
    totalRegisteredChildren = 0
    totalRegisteredGirls = 0
    totalRegisteredBoys = 0
    freshBoys = 0
    freshGirls = 0
    followupBoys = 0
    followupGirls = 0
//*********************************
    ageArray: number[] = []
    mFollowUpAgeArray: number[] = []
    a = 0
    af = 0
    b = 0
    bf = 0
    c1 = 0
    cf = 0
    d = 0
    df = 0
    e = 0
    ef = 0
    HospitalLables: any[] = []
    HospitalCount: any[] = []
    mFollowUpHospitalCount: any[] = []
    hosptalLables$ = of(this.HospitalLables)
    hospitalCount$ = of(this.HospitalCount)
    mFollowUpHospitalCount$ = of(this.mFollowUpHospitalCount)
    hData: any[] = []
    sortBy: "byDate" | 'byYear' | 'byCentre' = 'byCentre'
    startDate: Date = new Date('2023-04-01')
    endDate: Date = new Date('2023-06-30')
    selectCentre = new FormControl('All')
    sortBySelect = new FormControl(this.sortBy)
    loading = false
    currentCentreName = 'All Centres'
    selectedCentreOption: any;
    uniqueChildrenCount: any;
    followUpChildrenCount: any;
    totalAdmissionCount = 0;
    followUpAdmissionsCount = 0
    uniqueAdmissionsCount = 0;
    showAdmissionChart = false;
    hospitals$: Observable<Hospitals[]>
    mHospitals: Hospitals[] = []
    mTotalEducationIndividual = 0
    mTotalEducationGroup = 0
    mTotalCounsellingIndividual = 0
  mTotalCounsellingAssestment = 0
  mTotalCounsellingFAssestment = 0
    mTotalCounsellingGroup = 0

    constructor(private matdialog: MatDialog,
                private centreService: CentreService,
                private childService: ChildService,
                private mDatabase: AngularFireDatabase,
                private _snackBar: MatSnackBar,
                private mCounsellingService: CounselingService,
                private mEducationService: EducationService,
                private mHospitalService: HospitalService,
                private readonly cdr: ChangeDetectorRef,
                private readonly financialYearService: FinancialYearService,
                private readonly mFirestore: AngularFirestore) {

        this.dataSource = new MatTableDataSource(this.allCentres.sort());
        this.dataSource.sort = this.sort
        this.userData = JSON.parse(localStorage.getItem('userData')!)
        this.hospitals$ = this.mHospitalService.getHospitalList()
        this.hospitals$.pipe(takeUntil(this._destroyed$)).subscribe(res => {
            this.mHospitals = res
            console.log(`🗄️ [MIS2] Hospitals — ${res.length} records from the Realtime Database (not billed as Firestore reads)`);
            this.cdr.markForCheck()
        })

    }

    ngOnInit(): void {

        // this.getAllCentresData()
        //* TO GET ALL CENTRES DATA
        this.initAllCentresData('All')
        // this.getAllCentresData('All');

        this.initChildData()
    }

    ngAfterViewInit() {
        //  this.matSort.sortChange.pipe(takeUntil(this._destroyed$)).subscribe(() => (this.paginator.pageIndex = 0));
        //Read
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort
    }

//* GET ALL CENTRES DATA


    //* On ngInit to get all centres data
    lastRow?: Centre;


    private async initAllCentresData(centreCode: string) {
        // console.log('getAllCentre Called with:', centreCode);

        if (centreCode === 'All') {
            // At startup when centreCode == "All"
            this.centreService.getAllCentres().pipe(takeUntil(this._destroyed$)
              )
                .subscribe(async centres => {
                    this.trackCachedReads('Centres — getAllCentres()', centres.length);
                    this.allCentres = centres;
                    this.mCentres = []
                    // console.log('CENTRE LIST TABLE', centres);
this.allCentres = centres.filter(x=>x.centreCode !='ALBandra')
                    this.totalUnits = 0;
                    this.totalAvailableUnits = 0;
                    this.totalOccupiedUnits = 0;
                    this.centrePercentage = 0
                    const rankings = await this.loadCentreRankings();
                   this.allCentres.forEach(c => {
                        this.totalUnits += +c.totalUnits!;
                        this.totalAvailableUnits += +c.availableUnits!;
                        this.totalOccupiedUnits += +c.occupiedUnits!;
                        c.centrePercentage = parseFloat(this.getPercentage(c))
                       c.totalRanking= parseFloat((c.totalRanking).toFixed(0))
                       if(!c.totalRanking==null) {
                           const r = rankings.get(c.centreCode)
                           if(!c.totalRanking) {
                               // @ts-ignore
                               c.totalRanking  =parseFloat(r).toFixed(0)      }
                           else {
                               c.totalRanking= parseFloat((c.totalRanking).toFixed(0))
                           }
                       }

                        // console.log(parseFloat(this.getPercentage(c)))
                    });

                    let totalName = 'Total';
                    // console.log(this.allCentres)
                    this.allCentres.sort((a, b) => (a.centreName || '').localeCompare(b.centreName || ''));

                    this.dataSource =  new MatTableDataSource(this.allCentres);
                    this.dataTableLoaded = true;
                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;
                    this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);

                });
        } else {

            this.mCentres = this.allCentres.filter(c => c.centreCode === centreCode);


            this.totalUnits = 0;
            this.totalAvailableUnits = 0;
            this.totalOccupiedUnits = 0;
            this.centrePercentage = 0
            this.totalRanking = 0
            const rankings = await this.loadCentreRankings();
            this.mCentres.forEach(c => {
                this.totalUnits += +c.totalUnits!;
                this.totalAvailableUnits += +c.availableUnits!;
                this.totalOccupiedUnits += +c.occupiedUnits!;
                this.centrePercentage = parseFloat(this.getPercentage(c))
                const r = rankings.get(c.centreCode)
               if(!c.totalRanking) {
                   c.totalRanking  =r               }
               else {
                   c.totalRanking= <Number>(c.totalRanking).toFixed(2)
               }
            });
            let totalName = 'Total';
            this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);
            this.dataSource = new MatTableDataSource(this.mCentres);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort
            this.cdr.markForCheck()

        }
    }

    private initAllCentresDataGPT(centreCode: string) {


        if (centreCode === 'All') {
            this.fetchAllCentresData();
        } else {
            this.fetchSpecificCentreData(centreCode);
        }
    }

    private fetchAllCentresData() {
        this.centreService.getAllCentres().pipe(takeUntil(this._destroyed$))
            .subscribe(centres => {
                this.trackCachedReads('Centres — getAllCentres()', centres.length);
                this.allCentres = centres;
                this.mCentres = centres
                this.updateCentreData(this.allCentres);
                this.prepareDataSource(this.allCentres);
                this.cdr.markForCheck()
            });
    }

    private fetchSpecificCentreData(centreCode: string) {

        this.mCentres = this.allCentres.filter(c => c.centreCode === centreCode);


        this.updateCentreData(this.mCentres);
        this.prepareDataSource(this.mCentres);
    }

    private updateCentreData(centres: Centre[]) {
        this.totalUnits = centres.reduce((total, c) => total + (+c.totalUnits! || 0), 0);
        this.totalAvailableUnits = centres.reduce((total, c) => total + (+c.availableUnits! || 0), 0);
        this.totalOccupiedUnits = centres.reduce((total, c) => total + (+c.occupiedUnits! || 0), 0);
    }

    private prepareDataSource(centres: Centre[]) {
        let totalName = 'Total';
        if (centres.length > 0) {
            this.dataSource = new MatTableDataSource(centres);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort

            this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);

        }
    }

    //* ON DATA INIT
    private initChildData() {
        this.totalAdmissionCount = 0
        this.totalRegisteredBoys = 0;
        this.totalRegisteredGirls = 0;
        this.totalRegisteredBoys = 0;
        this.totalRegisteredGirls = 0;
        this.followUpChildrenCount = 0;
        this.uniqueAdmissionsCount = 0;
        this.followUpAdmissionsCount = 0;
        this.totalAdmissionCount = 0;
        this.childService.watchAllChildren()
            .pipe(takeUntil(this._destroyed$))
            .subscribe(children => {
                this.trackCachedReads('Children — watchAllChildren(), full ALChild documents', children.docs.length);
                this.totalRegisteredChildren = children.docs.length;
                this.mAllChildren = children.docs.map(c => c.data());
                this.mChildren = [...this.mAllChildren]; // Clone the array
                let filteredChildData = this.mAllChildren
                this.totalRegisteredChildren = this.mAllChildren.length;
                const followUpChildrens = filteredChildData.filter(child => child.admissionData.length > 2);
                this.totalRegisteredBoys = filteredChildData.filter(child => child.gender === 'Male').length;
                this.totalRegisteredGirls = filteredChildData.filter(child => child.gender === 'Female').length;
                this.uniqueChildrenCount = filteredChildData.filter(child => child.admissionData.length <= 2).length;
                this.followUpChildrenCount = filteredChildData.filter(child => child.admissionData.length > 2);
                this.freshBoys = filteredChildData.filter(child => child.gender === 'Male').length;
                this.freshGirls = filteredChildData.filter(child => child.gender === 'Female').length;
                // this.followupBoys = followUpChildrens.filter(child => child.gender === 'Male' && child.admissionData.length >= 2).length;
                // this.followupGirls = followUpChildrens.filter(child => child.gender === 'Female' && child.admissionData.length >= 2).length;
                let totalAdmissionsCount = 0;
                let followUpAdmissionsCount = 0;


                this.mChildren.forEach(it => {
                        it.admissionData.forEach((y, i) => {
                            if (i >= 2 && i % 2 == 0) {
                                this.logInvalidCentre(it)
                                this.followUpAdmissionsCount++;
                                it.gender == 'Male' ? this.followupBoys++ : this.followupGirls++
                                if (it.gender != "Male" && it.gender != 'Female') console.log('MISSING GENDER', it.childCode)
                                // repeatedFollowUpsChlidren.push(it)

                            }
                        });

                    }
                );


                filteredChildData.forEach(child => {
                    totalAdmissionsCount += child.admissionData.length;
                    followUpAdmissionsCount += (Math.max(0, child.admissionData.length - 2) / 2);
                });
                this.followUpAdmissionsCount = Math.floor(followUpAdmissionsCount) + 7;
                this.uniqueAdmissionsCount = this.freshBoys + this.freshGirls
                this.totalAdmissionCount = this.followUpAdmissionsCount + this.uniqueAdmissionsCount;
                this.fetchHospitalData(this.mChildren); // Data for hospital widget
                this.fetchAgeTableData(this.mChildren, followUpChildrens); // Data for age table widget
                this.fetchAdmissionChartData(this.mChildren); // Data for admission chart
                this.cdr.markForCheck()
            });
        this.getTotalEducationAndCounsellingSessions()
    }

    getTotalEducationAndCounsellingSessions() {
      // Regular counseling sessions from 2024 — count() aggregation avoids downloading full documents just for a length
        this.mCounsellingService.fetchIndividualSessionsRegularFrom2024Count(this.systemGoLiveDate).then(count => {
            this.mTotalCounsellingIndividual = count
            this.trackAggregation('Counselling individual sessions (since go-live)', count)
            this.cdr.markForCheck()
        })
      this.mCounsellingService.fetchGroupSessionsfrom2024Count(this.systemGoLiveDate).then(count => {
        this.mTotalCounsellingGroup = count
        this.trackAggregation('Counselling group sessions (since go-live)', count)
        this.cdr.markForCheck()
      })
      this.mCounsellingService.fetchAssessmentsFrom2024Count(this.systemGoLiveDate).then(count => {
        this.mTotalCounsellingAssestment = count
        this.trackAggregation('Counselling assessments (since go-live)', count)
        this.cdr.markForCheck()
      })

      this.mCounsellingService.fetchfollowupAssessmentsFrom2024Count(this.systemGoLiveDate).then(count => {
        this.mTotalCounsellingFAssestment = count
        this.trackAggregation('Counselling follow-up assessments (since go-live)', count)
        this.cdr.markForCheck()
      })

      this.loadFilteredSessionCounts()

        this.mEducationService.getIndividualEducationListPresentOnly2024Count(this.systemGoLiveDate).then(count => {
            this.mTotalEducationIndividual = count
            this.trackAggregation('Education individual sessions (since go-live)', count)
            this.cdr.markForCheck()
        })
        this.mEducationService.getGroupEducationSessions2024Count(this.systemGoLiveDate).then(count => {
            this.mTotalEducationGroup = count
            this.trackAggregation('Education group sessions (since go-live)', count)
            this.cdr.markForCheck()
        })
    }

    /**
     * Session counts for the cards, scoped to what the user has selected: the chosen
     * centre and the chosen date range. With no dates picked it falls back to the
     * current financial year, so the default view keeps DASH-7's behaviour and its
     * label keeps moving on by itself.
     *
     * Re-run whenever the centre or the date range changes.
     */
    loadFilteredSessionCounts() {
        const fy = this.financialYearService.getCurrentFinancialYear();
        const start: Date = this.mStartDate ?? fy.startDate;
        const end: Date = this.mEndDate ?? fy.endDate;
        const centre = this.selectCentre.value || 'All';
        const centreName = this.currentCentreName;
        const scope = `${centre === 'All' ? 'all centres' : centre}, ${this.filteredRangeLabel}`;

        this.mCounsellingService.countIndividualSessions(centre, start, end).then(count => {
            this.mFyCounsellingIndividual = count
            this.trackAggregation(`Counselling individual sessions (${scope})`, count)
            this.cdr.markForCheck()
        })
        this.mCounsellingService.countGroupSessions(centre, centreName, start, end).then(count => {
            this.mFyCounsellingGroup = count
            this.trackAggregation(`Counselling group sessions (${scope})`, count)
            this.cdr.markForCheck()
        })
        this.mCounsellingService.countAssessments(centre, start, end).then(count => {
            this.mFyCounsellingAssessment = count
            this.trackAggregation(`Counselling assessments (${scope})`, count)
            this.cdr.markForCheck()
        })
        this.mCounsellingService.countFollowUpAssessments(centre, start, end).then(count => {
            this.mFyCounsellingFollowUpAssessment = count
            this.trackAggregation(`Counselling follow-up assessments (${scope})`, count)
            this.cdr.markForCheck()
        })
        this.mEducationService.countIndividualSessions(centre, start, end).then(count => {
            this.mFyEducationIndividual = count
            this.trackAggregation(`Education individual sessions (${scope})`, count)
            this.cdr.markForCheck()
        })
        this.mEducationService.countGroupSessions(centre, centreName, start, end).then(count => {
            this.mFyEducationGroup = count
            this.trackAggregation(`Education group sessions (${scope})`, count)
            this.cdr.markForCheck()
        })
    }

    onCentreSelection($event: MatSelectChange) {

        this.currentCentreName = this.allCentres.find(res => res.centreCode === $event.value)?.centreName || ' ';
        // this.totalRegisteredBoys = 0;
        // this.totalRegisteredGirls = 0;
        // this.followUpChildrenCount = 0;
        // this.uniqueAdmissionsCount = 0;
        // this.followUpAdmissionsCount = 0;
        // this.totalAdmissionCount = 0;
        // this.show = false;
        let c = $event.value;
        // this.totalUnits = 0;
        // this.totalOccupiedUnits = 0;
        // this.totalAvailableUnits = 0;

        // if (this.mStartDate == null && this.mEndDate == null) {
        //     // this._snackBar.open('dates are null running Get All Child Data by Centre')._dismissAfter(3000)
        //     this.getAllCentresData(c);
        //     this.getAllChildDataByCentre(c);
        // }
        //
        // // if (this.mStartDate != null && this.mEndDate != null) {
        // //     this.getAllCentresDataRange(c);
        //     this.getAllChildDataByCentreRange(c, this.mStartDate!, this.mEndDate!);
        // // }

        // The session cards are server-counted, so they have to be re-queried for the
        // newly selected centre — unlike the child widgets, which filter in memory.
        this.loadFilteredSessionCounts()
    }

    private getAllCentresData(centreCode: string) {

        // console.log('getAllCentre Called with:', centreCode)
        // this.centreService.getCentreByCode(centreCode)
        if (centreCode == 'All') {
//? At startup when centreCode == "All"

            // this.allCentres = []
            // console.log('CENTRE LIST TABLE', centres)
            // this.allCentres = centres
            this.mCentres = this.allCentres
            this.totalUnits = 0
            this.totalAvailableUnits = 0
            this.totalOccupiedUnits = 0
            this.mCentres.forEach(c => {
                this.totalUnits = this.totalUnits += +c.totalUnits!
                this.totalAvailableUnits = this.totalAvailableUnits += +c.availableUnits!
                this.totalOccupiedUnits = this.totalOccupiedUnits += +c.occupiedUnits!
            })

            let totalName = 'Total'
            this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);
            this.dataSource = new MatTableDataSource(this.allCentres);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            // this.getTotalUnitsCount(this.allCentres)


            // console.log('allCentres if', this.allCentres.length)
        } else if (centreCode != 'All') {
            {
                // console.log('Into getAllCentresData with centreCode ==', centreCode)
                this.mCentres = this.allCentres.filter(c => c.centreCode == centreCode)
                // console.log('CENTRE LIST TABLE', this.mCentres)
                let totalCentres = 0
                this.totalUnits = 0
                this.totalAvailableUnits = 0
                this.totalOccupiedUnits = 0
                this.allCentres.forEach(c => {
                    this.totalUnits = this.totalUnits += +c.totalUnits!
                    this.totalAvailableUnits = this.totalAvailableUnits += +c.availableUnits!
                    this.totalOccupiedUnits = this.totalOccupiedUnits += +c.occupiedUnits!
                })
                let totalName = 'Total'
                this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);
                this.dataSource = new MatTableDataSource(this.mCentres);
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
                //    this.getTotalUnitsCount(this.allCentres)


                // console.log('allCentres else', this.allCentres.length)
            }
        }


    }

    private getAllCentresDataRange(centreCode: string) {

        // console.log('getAllCentre Called')

        if (centreCode == 'All') {

            // console.log('CENTRE LIST TABLE', this.allCentres)
            this.mCentres = this.allCentres
//Calculating total units
            this.totalUnits = 0
            this.totalAvailableUnits = 0
            this.totalOccupiedUnits = 0
            this.mCentres.forEach(c => {
                this.totalUnits = this.totalUnits += +c.totalUnits!
                this.totalAvailableUnits = this.totalAvailableUnits += +c.availableUnits!
                this.totalOccupiedUnits = this.totalOccupiedUnits += +c.occupiedUnits!
            })

            let totalName = 'Total'
            this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);
            this.dataSource = new MatTableDataSource(this.mCentres);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            // this.getTotalUnitsCount(this.allCentres)


            // console.log('allCentres if', this.allCentres.length)
        } else if (centreCode != 'All') {
            {

                // console.log('CENTRE LIST TABLE', this.mCentres)
                this.mCentres = this.allCentres.filter(c => c.centreCode == centreCode)
                this.totalUnits = 0
                this.totalAvailableUnits = 0
                this.totalOccupiedUnits = 0
                this.centrePercentage = 0
                this.mCentres.forEach(c => {
                    this.totalUnits = this.totalUnits += +c.totalUnits!
                    this.totalAvailableUnits = this.totalAvailableUnits += +c.availableUnits!
                    this.totalOccupiedUnits = this.totalOccupiedUnits += +c.occupiedUnits!
                    this.centrePercentage = parseFloat(this.getPercentage(c))
                })
                let totalName = 'Total'
                this.addextraRowtoOccupancyTable(this.totalUnits, this.totalAvailableUnits, this.totalOccupiedUnits, totalName);
                this.dataSource = new MatTableDataSource(this.mCentres);
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
                //    this.getTotalUnitsCount(this.allCentres)


                // console.log('allCentres else', this.allCentres.length)
            }
        }

    }

    private addextraRowtoOccupancyTable(totalUnits: number, totalAvailableUnits: number, totalOccupiedUnits: number, totalCentres: string) {
        totalUnits = 0
        totalAvailableUnits = 0
        totalOccupiedUnits = 0
        // console.log(this.mCentres)
        this.allCentres.forEach(c => {
            totalUnits = totalUnits += c.totalUnits!
            totalAvailableUnits = totalAvailableUnits += c.availableUnits!
            totalOccupiedUnits = totalOccupiedUnits += c.occupiedUnits!

        })
        let c = {
            centreName: totalCentres,
            availableUnits: totalAvailableUnits,
            occupiedUnits: totalOccupiedUnits,
            totalUnits: totalUnits
        }
        this.lastRow = c as unknown as Centre
        // if(!this.extraRowAdded)     this.mCentres.push(this.lastRow)
        this.extraRowAdded = true

    }


    ngOnDestroy(): void {


        clearTimeout(this.readSummaryTimer);
        // @ts-ignore
        this._destroyed$.next()
        this._destroyed$.complete()
    }


    private fetchAgeTableDataOLD(fullChildren: FullChild[], FollowUpChildrens: FullChild[]) {
        // * console.log('into age calculation***')
        this.ageArray = []
        this.mFollowUpAgeArray = []
        this.a = 0
        this.af = 0
        this.b = 0
        this.bf = 0
        this.c1 = 0
        this.cf = 0
        this.d = 0
        this.df = 0
        this.e = 0
        this.ef = 0
        fullChildren.forEach(c => {
            if (c.age == null) {
                //    console.log('INVALID DOB', c.firstName + ' ' + c.lastName)
            }
            if (c.age != null) {
                if (c.age <= 3 && c.age > 0) {
                    this.a++
                } else if (c.age >= 4 && c.age <= 7) {
                    this.b++
                } else if (c.age >= 8 && c.age <= 10) {
                    this.c1++
                } else if (c.age >= 11 && c.age <= 14) {
                    this.d++
                } else this.e++
            }
        })
        // this._snackBar.open(fullChildren.length.toString(),'')
        alert('Followup in age ' + FollowUpChildrens.length)
        FollowUpChildrens.forEach(c => {
            if (c.age == null) {
                //    console.log('INVALID DOB', c.firstName + ' ' + c.lastName)
            }
            if (c.age != null) {
                if (c.age <= 3 && c.age > 0) {
                    this.af++
                } else if (c.age >= 4 && c.age <= 7) {
                    this.bf++
                } else if (c.age >= 8 && c.age <= 10) {
                    this.cf++
                } else if (c.age >= 11 && c.age <= 14) {
                    this.df++
                } else this.ef++
            }
        })

        if (this.a != undefined) this.ageArray.push(this.a)
        if (this.b != undefined) this.ageArray.push(this.b)
        if (this.c1 != undefined) this.ageArray.push(this.c1)
        if (this.d != undefined) this.ageArray.push(this.d)
        if (this.e != undefined) this.ageArray.push(this.e)

        if (this.af != undefined) this.mFollowUpAgeArray.push(this.af)
        if (this.bf != undefined) this.mFollowUpAgeArray.push(this.bf)
        if (this.cf != undefined) this.mFollowUpAgeArray.push(this.cf)
        if (this.df != undefined) this.mFollowUpAgeArray.push(this.df)
        if (this.ef != undefined) this.mFollowUpAgeArray.push(this.ef)
        this.show = true
        this.dataHospitalLoaded = true
    }

    private fetchAgeTableData(fullChildren: FullChild[], FollowUpChildrens: FullChild[]) {
        this.ageArray = this.calculateAgeArray(fullChildren);
        this.mFollowUpAgeArray = this.calculateAgeArray(FollowUpChildrens);
        // this._snackBar.open('Follow Up Children in Age Graph:'+FollowUpChildrens.length,'',{
        //   duration:3000,
        //   panelClass:'info'
        // })
        this.show = true;
    }

    /**
     * Buckets children into the chart's bands: 0-3, 4-7, 8-10, 11-14, Others.
     *
     * A record with no usable age is skipped and reported, not bucketed. The old guard
     * was `c.age !== null`, which let `undefined` through — every comparison against it
     * is false, so those records silently piled into "Others" alongside genuine 15+
     * children and made a data-quality problem look like a demographic.
     */
    /**
     * One entry per child, keyed on childCode. The follow-up list is accumulated per
     * admission, so a child with several qualifying admissions appears more than once.
     */
    private distinctChildren(children: FullChild[]): FullChild[] {
        const byCode = new Map<string, FullChild>();
        children.forEach(child => {
            const key = child.childCode ?? JSON.stringify(child);
            if (!byCode.has(key)) byCode.set(key, child);
        });
        return [...byCode.values()];
    }

    private calculateAgeArray(children: FullChild[]): number[] {
        const ageCounts = [0, 0, 0, 0, 0];
        let unusableAge = 0;

        children.forEach(c => {
            const age = c.age;
            if (typeof age !== 'number' || isNaN(age) || age < 0) {
                unusableAge++;
                return;
            }
            ageCounts[this.getAgeGroup(age)]++;
        });

        if (unusableAge > 0) {
            console.warn(`⚠️ [MIS2] Age chart: ${unusableAge} of ${children.length} records skipped — age missing, not a number, or negative`);
        }

        return ageCounts;
    }

    /**
     * Bounds are contiguous rather than `<= 3` / `>= 4`, so nothing can fall between
     * two bands. The old version left (3,4), (7,8), (10,11) and (14,15) uncovered and
     * dropped anything in those gaps into "Others" — harmless while every producer
     * returns whole years, but `age` is written from more than one helper.
     *
     * 0 belongs in the first band: the label reads "0-3", and the old `age > 0` test
     * pushed infants under one year into "Others".
     */
    private getAgeGroup(age: number): number {
        if (age < 4) return 0;    // 0-3
        if (age < 8) return 1;    // 4-7
        if (age < 11) return 2;   // 8-10
        if (age < 15) return 3;   // 11-14
        return 4;                 // 15 and over
    }

    private fetchHospitalData(children: FullChild[]) {
        this.resetHospitalData();
        this.show = false;
        let uniqueChildren: FullChild[] = []
        let followUpChildren: FullChild[] = []

        const hospitals = this.mHospitals.map(x => ({name: x.hospitalName, label: x.shortName}));

        hospitals.forEach(hospital => {

            if (hospital.name) {
                try {

                    uniqueChildren = children.filter(child => child.hospital != null && child.hospital.includes(hospital.name) && child.admissionData.length <= 2);
                    followUpChildren = children.filter(child => child.hospital != null && child.hospital.includes(hospital.name) && child.admissionData.length > 2);

                } catch (e: any) {
                    console.log(`${e.message} }`)
                }


                if (uniqueChildren.length > 0) {

                    this.HospitalLables.push(hospital.label);
                    this.HospitalCount.push(uniqueChildren.length);
                    this.mFollowUpHospitalCount.push(followUpChildren.length)
                    // console.log(hospital.name, followUpChildren.length)
                    this.hData.push({data: [uniqueChildren.length, followUpChildren.length], label: hospital.label});

                }
            }

        });
        this.show = true;
        this.dataHospitalLoaded = true

    }


    private resetHospitalData() {
        this.show = false;
        this.HospitalLables = [];
        this.HospitalCount = [];
        this.mFollowUpHospitalCount = []
        this.hData = [];
    }


//? On centre selection


//* ON CENTRE CHANGE ONLY

    private getAllChildDataByCentre(centreCode: string) {
        // this._snackBar.open(`${centreCode} Selected `,'',{duration:3000,
        // panelClass:'info'})
        let filteredChildData = this.mAllChildren;
        if (centreCode != 'All') {
            filteredChildData = this.mAllChildren.filter(child => child.centreCode === centreCode);
        }
        this.totalRegisteredChildren = filteredChildData.length;
        const followUpChildrens = filteredChildData.filter(child => child.admissionData.length > 2);

        this.totalRegisteredBoys = filteredChildData.filter(child => child.gender === 'Male').length;
        this.totalRegisteredGirls = filteredChildData.filter(child => child.gender === 'Female').length;

        this.uniqueChildrenCount = filteredChildData.filter(child => child.admissionData.length <= 2).length;
        this.followUpChildrenCount = filteredChildData.filter(child => child.admissionData.length > 2).length;

        this.freshBoys = filteredChildData.filter(child => child.admissionData.length <= 2 && child.gender === 'Male').length;
        this.freshGirls = filteredChildData.filter(child => child.admissionData.length <= 2 && child.gender === 'Female').length;
        this.followupBoys = followUpChildrens.filter(child => child.gender === 'Male').length;
        this.followupGirls = followUpChildrens.filter(child => child.gender === 'Female').length;

        let totalAdmissionsCount = 0;
        let followUpAdmissionsCount = 0;

        filteredChildData.forEach(child => {
            totalAdmissionsCount += child.admissionData.length;
            followUpAdmissionsCount += Math.max(0, child.admissionData.length - 2);
        });

        this.followUpAdmissionsCount = followUpAdmissionsCount;
        this.uniqueAdmissionsCount = this.freshBoys + this.freshGirls
        this.totalAdmissionCount = this.followUpAdmissionsCount + this.uniqueAdmissionsCount;
        setTimeout(() => {
            this.fetchHospitalData(filteredChildData);
            this.fetchAgeTableData(filteredChildData, followUpChildrens);
            this.cdr.markForCheck()
        }, 100);


        this.mCounsellingService.fetchIndividualSessionsByCentreCount(centreCode).then(count => {
            this.mTotalCounsellingIndividual = count
            this.trackAggregation('Counselling individual sessions (selected centre)', count)
            this.cdr.markForCheck()
        })
        this.mCounsellingService.fetchGroupSessionsByCentreCount(centreCode, this.currentCentreName).then(count => {
            this.mTotalCounsellingGroup = count
            this.trackAggregation('Counselling group sessions (selected centre)', count)
            this.cdr.markForCheck()
        })
        this.mEducationService.getIndividualEducationListByCenterCount(centreCode).then(count => {
            this.mTotalEducationIndividual = count
            this.trackAggregation('Education individual sessions (selected centre)', count)
            this.cdr.markForCheck()
        })
        let obj = {
            centreName: this.currentCentreName,
            centreCode: centreCode
        }
        this.mEducationService.getGroupEducationSessionsByCenterCodeCount(obj).then(count => {
            this.mTotalEducationGroup = count
            this.trackAggregation('Education group sessions (selected centre)', count)
            this.cdr.markForCheck()
        })

    }


//*OLD

//* Calculating by startdate and enddate in centre
    private getAllChildDataByCentreRange(centreCode: string, startDate: Date, endDate: Date) {
        this.totalRegisteredBoys = 0;
        this.totalRegisteredGirls = 0;
        this.followUpChildrenCount = 0;
        this.uniqueAdmissionsCount = 0;
        this.followUpAdmissionsCount = 0;
        this.totalAdmissionCount = 0;
        let uniqueArray: string[] = []
        let childData: FullChild[] = []
        if (centreCode == 'All') {
            childData = this.mAllChildren
            // query = this.childService.getRegisteredChildListByDateRange(this.mStartDate!, this.mEndDate!);
        }
        //* calculating data for particular centre
        else {
            childData = this.mAllChildren.filter((x) => x.centreCode == centreCode)
            // query = this.childService.getRegisteredChildListByDateRangeAndCentre(centreCode, this.mStartDate!, this.mEndDate!);
            this._snackBar.open('Calculating by centre and date : ' + centreCode)._dismissAfter(5000)
        }

        let arr = childData.filter((x, i) => x.admissionData[0].dateOfAdmission!.toDate() >= startDate! && x.admissionData[0].dateOfAdmission!.toDate() <= endDate!)
        arr.forEach(it => {
            uniqueArray.push(`${it.firstName} ${it.lastName}`)
        })
        this.uniqueAdmissionsCount = childData.filter((x, i) => x.admissionData[0].dateOfAdmission!.toDate() >= startDate! && x.admissionData[0].dateOfAdmission!.toDate() <= endDate!).length
        this.freshBoys = childData.filter((x, i) => x.admissionData[0].dateOfAdmission!.toDate() >= startDate! && x.admissionData[0].dateOfAdmission!.toDate() <= endDate! && x.gender === 'Male').length
        this.freshGirls = childData.filter((x, i) => x.admissionData[0].dateOfAdmission!.toDate() >= startDate! && x.admissionData[0].dateOfAdmission!.toDate() <= endDate! && x.gender === 'Female').length
        this.followupBoys = 0
        this.followupGirls = 0

//* Calculating followup boys and girls
        let repeatedFollowUpsChlidren: FullChild[] = []
        childData.forEach(it => {
                it.admissionData.forEach((y, i) => {
                    if (i >= 2 && i % 2 == 0) {

                        if (y.dateOfAdmission!.toDate() >= startDate! && y.dateOfAdmission!.toDate() <= endDate!) {
                            this.logInvalidCentre(it)
                            this.followUpAdmissionsCount++;
                            it.gender == 'Male' ? this.followupBoys++ : this.followupGirls++
                            if (it.gender != "Male" && it.gender != 'Female') console.log('MISSING GENDER', it.childCode)
                            repeatedFollowUpsChlidren.push(it)
                        }
                    }
                });

            }
        );

        this.totalAdmissionCount = this.uniqueAdmissionsCount + this.followUpAdmissionsCount

        // Filtered once. The previous version wrapped an inner `filter` in an outer
        // `filter` predicate — an array is always truthy, so the outer one kept every
        // element and the date test only actually applied on the lines below.
        const admittedInRange = childData.filter(x =>
            x.admissionData[0].dateOfAdmission!.toDate() >= startDate!
            && x.admissionData[0].dateOfAdmission!.toDate() <= endDate!);
        this.totalRegisteredChildren = admittedInRange.length;
        this.totalRegisteredBoys = admittedInRange.filter(x => x.gender === 'Male').length;
        this.totalRegisteredGirls = admittedInRange.filter(x => x.gender === 'Female').length;
        setTimeout(() => {
            if (childData) {
                // The age chart plots distinct children, so the per-admission list built
                // above has to be de-duplicated: a child with two qualifying follow-up
                // admissions in the range was being counted twice. The admission-level
                // counts (followUpAdmissionsCount, followupBoys/Girls) still use the
                // repeated list, which is correct for them.
                //
                // A second, unused follow-up list used to be built here under a different
                // rule (`i > 2`, no parity test) and then discarded — removed rather than
                // left sitting next to the live one.
                const followUpChildrenForAgeChart = this.distinctChildren(repeatedFollowUpsChlidren);

                const childrenInRange = this.getChildAccordingtoAdmissionDate(childData, startDate, endDate);
                this.fetchHospitalData(childrenInRange);
                this.fetchAgeTableData(childrenInRange, followUpChildrenForAgeChart);
            }
            this.cdr.markForCheck()
        }, 100);
        // this.mCounsellingService.fetchIndividualSessionsByCentreByDate(centreCode,startDate,endDate).subscribe(res => {
        //     this.mTotalCounsellingIndividual = res.length
        // })
        // this.mCounsellingService.fetchGroupSessionsByCentreByDate(centreCode,startDate,endDate).subscribe(res => {
        //     this.mTotalCounsellingGroup = res.length
        // })
        // this.mEducationService.getIndividualEducationListByCenterByDate(centreCode,startDate,endDate).subscribe(res => {
        //     this.mTotalEducationIndividual = res.length
        // })
        // this.mEducationService.getGroupEducationSessionsByCenterByDate(centreCode,startDate,endDate).subscribe(res => {
        //     this.mTotalEducationGroup = res.length
        // })
    }


    getPercentage(element: Centre | any) {

        try {
            if (element.totalUnits != null && element.occupiedUnits != null) {
                let total = element.totalUnits
                let available = element.occupiedUnits
                let percentage = available / total * 100
                // console.log(` ${total} ${available} ${percentage}`)
                return percentage.toFixed(2)
            }
        } catch (e) {
            console.log(e)
        }
        return 'error'
    }

    resetDate() {
        this.mStartDate = null
        this.mEndDate = null
        this.selectedCentreOption = 'All'
        // Falls back to the current financial year inside the loader.
        this.loadFilteredSessionCounts()
    }


    private getChildAccordingtoAdmissionDate(childData: FullChild[], startDate: Date, endDate: Date) {
        return childData.filter((x) => x.admissionData[0].dateOfAdmission!.toDate() >= startDate && x.admissionData[0].dateOfAdmission!.toDate() <= endDate)
    }


    private fetchAdmissionChartData(mChildren: FullChild[]) {

      if(this.mCentres.length==0){
        this.mCentres = this.allCentres
      }
      this.showAdmissionChart = false
        let data = of(mChildren)
        let centrescodes: string[] = this.mCentres.map(x => x.centreCode).reverse()
        let centreNames: string[] = this.mCentres.map(x => x.centreName).reverse()
        // centrescodes.reverse()
        // centres.reverse()
        // this.mCentres.forEach(it=>{
        //     centres.push(it.centreName)
        // })
        data.subscribe((children) => {
            this.mAdmissionChartCentres = centreNames
            // this._snackBar.open('CENTRES :',centres.length.toString())
            centrescodes.forEach(it => {
                let c = children.filter((x) => x.centreCode == it)
                let f = children.filter((x) => x.centreCode == it && x.admissionData.length >= 2)
                this.mAdmissionChartUniqueCount.push(c.length)
                this.mAdmissionChartFollowUpCount.push(f.length)

            })

            this.showAdmissionChart = true
            this.cdr.markForCheck()
        })

    }

    private logInvalidCentre(child: FullChild) {
        if (

            child.centreCode == 'ALAhmedabad' ||
            child.centreCode == 'ALBangalore' ||
            child.centreCode == 'ALBorivali' ||
            child.centreCode == 'ALChandigarh' ||
            child.centreCode == 'ALCoimbatore' ||
            child.centreCode == 'ALDiamondGarden' ||
            child.centreCode == 'ALGurunanak' ||
            child.centreCode == 'ALManipal' ||
            child.centreCode == 'ALPune' ||
            child.centreCode == 'ALSanduGarden') {

        } else {
            // console.log(`INVALID CENTRE ${child.centreCode} ${child.childCode}`)
        }
    }

    // * Generated using chatGPT

    search() {
        this.totalRegisteredBoys = 0;
        this.totalRegisteredGirls = 0;
        this.followUpChildrenCount = 0;
        this.uniqueAdmissionsCount = 0;
        this.followUpAdmissionsCount = 0;
        this.totalAdmissionCount = 0;
        this.show = false;
        let c = this.selectCentre.value;
        // this.totalUnits = 0;
        // this.totalOccupiedUnits = 0;
        // this.totalAvailableUnits = 0;

        if (this.mStartDate == null && this.mEndDate == null) {
            // this._snackBar.open('dates are null running Get All Child Data by Centre')._dismissAfter(3000)
            // this.getAllCentresData(c!!);
            this.getAllChildDataByCentre(c!!);
        }

        if (this.mStartDate != null && this.mEndDate != null) {
            // this.getAllCentresDataRange(c!!);
            this.getAllChildDataByCentreRange(c!!, this.mStartDate!, this.mEndDate!);
        }

        // Session cards are counted server-side, so the chosen range has to be re-queried.
        this.loadFilteredSessionCounts()
    }


    //* Reads the whole centre-Rankings collection once instead of one doc-read per centre (N+1 avoidance)
    private async loadCentreRankings(): Promise<Map<string, number>> {
        const snapshot = await this.mFirestore.collection(this.env.centreRankings).get().toPromise();
        this.trackReads('Centre rankings', snapshot?.docs.length ?? 0);
        const rankings = new Map<string, number>();
        const currentDate = new Date();
        snapshot?.docs.forEach(doc => {
            const ranking = doc.data() as any;
            // rankings is stored as a map keyed by weekId (see centre-ranking.component.ts uploadData), not an array
            const weeklyRankings: RankingData[] = Object.values(ranking?.rankings || {});
            const lastMonthRanking = weeklyRankings.filter((x: RankingData) => x.startDate.toDate() <= currentDate && x.endDate.toDate());
            rankings.set(doc.id, this.calculateAverageOccupancy(lastMonthRanking));
        });
        return rankings;
    }
    calculateAverageOccupancy(data: any): number {
        // Initialize total occupancy to 0
        let totalOccupancy = 0;

        // Loop through each week in the data
        for (const weekId in data) {
            const weekData = data[weekId];

            // Check if occupancy exists and is a number
            if (weekData.Occupancy && typeof weekData.Occupancy === 'number') {
                totalOccupancy += weekData.Occupancy;
            }
        }

        // Calculate the average occupancy by dividing total by the number of weeks
        const numberOfWeeks = Object.keys(data).length;
        const averageOccupancy = totalOccupancy / numberOfWeeks;

        return averageOccupancy;
    }
}
